import {
	queryOptions,
	useMutation,
	useQuery,
	useQueryClient,
	useSuspenseQuery,
} from "@tanstack/react-query";
import {
	fetchStudentConversations,
	fetchConversationMessages,
	postConversationMessage,
	postConversation,
	fetchStudentConversationCourseList,
	fetchStudentConversationCreateTypeList,
	fetchStudentInterventionSummary,
	updateStudentInterventionSummary,
} from "./api";
import {
	Conversation,
	CreateConversationForStudentRequestFlat,
	CreateMessageForConversationRequestFlat,
	GetConversationsForStudentRequestFlat,
	GetConversationTypesForStudentRequestFlat,
	GetMessagesForConversationRequestFlat,
	GetStudentInterventionSummaryRequestFlat,
	StudentInterventionSummary,
	UpdateStudentInterventionSummaryRequestFlat,
} from "@godalming/api-types";
import {
	getEffectiveUsernameOverride,
	meSessionQueryOptions,
} from "@features/me/queries";
import type { MessageFormat } from "./messageDocument";

export const studentConversationsQueryOptions = (
	params: GetConversationsForStudentRequestFlat,
) => {
	const override = getEffectiveUsernameOverride();
	return queryOptions({
		queryKey: ["students", params.studentNo, "conversations", override],
		queryFn: () => fetchStudentConversations(params, override),
		staleTime: 600000,
		refetchInterval: 30000,
		refetchOnWindowFocus: "always",
	});
};

export function useStudentConversationsQuery(
	params: GetConversationsForStudentRequestFlat,
) {
	return useQuery(studentConversationsQueryOptions(params));
}

export function useStudentConversationsSuspense(
	params: GetConversationsForStudentRequestFlat,
) {
	return useSuspenseQuery(studentConversationsQueryOptions(params));
}

export const conversationMessagesQueryOptions = (
	params: GetMessagesForConversationRequestFlat,
) =>
	queryOptions({
		queryKey: ["conversations", params.conversationId, "messages"],
		queryFn: () => fetchConversationMessages(params),
		select: (msgs) =>
			[...msgs].sort((a, b) => {
				const ta = new Date(a.dateEntered).getTime();
				const tb = new Date(b.dateEntered).getTime();
				return ta - tb;
			}),
	});

export function useConversationMessagesQuery(
	params: GetMessagesForConversationRequestFlat,
) {
	return useQuery(conversationMessagesQueryOptions(params));
}

export function useConversationMessagesSuspense(
	params: GetMessagesForConversationRequestFlat,
) {
	return useSuspenseQuery(conversationMessagesQueryOptions(params));
}

export const conversationTypesQueryOptions = (
	params: GetConversationTypesForStudentRequestFlat,
) =>
	queryOptions({
		queryKey: ["students", params.studentNo, "conversationTypes"],
		queryFn: () => fetchStudentConversationCreateTypeList(params),
		staleTime: Infinity,
	});

export function useConversationTypesQuery(
	params: GetConversationTypesForStudentRequestFlat,
) {
	return useQuery(conversationTypesQueryOptions(params));
}

export function useConversationTypesSuspense(
	params: GetConversationTypesForStudentRequestFlat,
) {
	return useSuspenseQuery(conversationTypesQueryOptions(params));
}

export const conversationCoursesQueryOptions = (
	params: GetConversationTypesForStudentRequestFlat,
) =>
	queryOptions({
		queryKey: ["students", params.studentNo, "conversationCourses"],
		queryFn: () => fetchStudentConversationCourseList(params),
		staleTime: Infinity,
	});

export function useConversationCoursesQuery(
	params: GetConversationTypesForStudentRequestFlat,
) {
	return useQuery(conversationCoursesQueryOptions(params));
}

export function useConversationCoursesSuspense(
	params: GetConversationTypesForStudentRequestFlat,
) {
	return useSuspenseQuery(conversationCoursesQueryOptions(params));
}

export const studentInterventionSummaryQueryOptions = (
	params: GetStudentInterventionSummaryRequestFlat,
) =>
	queryOptions<StudentInterventionSummary>({
		queryKey: ["students", params.studentNo, "interventionSummary"],
		queryFn: () => fetchStudentInterventionSummary(params),
	});

export function useStudentInterventionSummaryQuery(
	params: GetStudentInterventionSummaryRequestFlat,
) {
	return useQuery(studentInterventionSummaryQueryOptions(params));
}

export function useStudentInterventionSummarySuspense(
	params: GetStudentInterventionSummaryRequestFlat,
) {
	return useSuspenseQuery(studentInterventionSummaryQueryOptions(params));
}

export function useUpdateStudentInterventionSummaryMutation() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (vars: UpdateStudentInterventionSummaryRequestFlat) =>
			updateStudentInterventionSummary(vars),
		onSettled: (_data, _error, variables) => {
			queryClient.invalidateQueries({
				queryKey: ["students", variables.studentNo, "interventionSummary"],
			});
		},
	});
}
export function useCreateMessageMutation() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (
			vars: CreateMessageForConversationRequestFlat & {
				studentNo: string;
				messageFormat?: MessageFormat;
			},
		) => postConversationMessage(vars),
		onSuccess: async (_data, vars) => {
			await Promise.all([
				queryClient.invalidateQueries({
					queryKey: ["conversations", vars.conversationId.toString()],
				}),
				queryClient.invalidateQueries(
					studentConversationsQueryOptions({ studentNo: vars.studentNo }),
				),
			]);
		},
	});
}

export function useCreateConversationMutation() {
	const queryClient = useQueryClient();

	return useMutation({
		// Called when you do mutate(variables)
		mutationFn: async (
			vars: CreateConversationForStudentRequestFlat & {
				messageFormat?: MessageFormat;
			},
		) => postConversation(vars),

		onMutate(variables) {
			const now = new Date().toISOString();

			// Stop in-flight fetches from overwriting your optimistic write
			queryClient.cancelQueries(
				studentConversationsQueryOptions({
					studentNo: variables.studentNo.toString(),
				}),
			);

			// Snapshot for rollback
			const prevConversations = queryClient.getQueryData<Conversation[]>(
				studentConversationsQueryOptions({
					studentNo: variables.studentNo.toString(),
				}).queryKey,
			);

			const optimisticConversation: Conversation = {
				dateCreated: now,
				dateLastEntered: now,
				idConversation: -1, // Temporary ID; real ID comes from server
				nameTypeCfC: "Loading...",
				descSubject: null,
				isConversationClosed: false,
				createdBy:
					queryClient.getQueryData(meSessionQueryOptions().queryKey)
						?.actualStaffProfile?.initials ?? "NA",
			};

			queryClient.setQueryData<Conversation[]>(
				studentConversationsQueryOptions({
					studentNo: variables.studentNo.toString(),
				}).queryKey,
				(old = []) => [optimisticConversation, ...old],
			);

			return { studentNo: variables.studentNo, prevConversations };
		},

		onError: (err, _vars, context) => {
			if (!context) return;
			queryClient.setQueryData(
				studentConversationsQueryOptions({
					studentNo: context.studentNo.toString(),
				}).queryKey,
				context.prevConversations,
			);
		},

		// Update caches when the mutation succeeds
		onSettled(_data, _error, variables) {
			queryClient.invalidateQueries({
				queryKey: ["students", variables.studentNo.toString(), "conversations"],
			});
		},
	});
}
